import React, {useState, useEffect} from "react";
import {Form, Input, Button, Checkbox, InputNumber, Row, Col, Divider} from "antd";
import MaskedInput from "antd-mask-input";
import {useObserver} from "mobx-react-lite";
import {useFetch} from "hooks/useFetch";
import {ZoneSelect} from "components/_defaultComponents/ZoneSelect";
import DynamicCityFilter from "components/_defaultComponents/FilterComponents/DynamicCityFilter";
import DynamicDistrictFilter from "components/_defaultComponents/FilterComponents/DynamicDistrictFilter";
import DynamicNeighborhoodFilter from "components/_defaultComponents/FilterComponents/DynamicNeighborhoodFilter";
import {useStore} from "hooks/useStore";
import {ResponsiveDataTableStyled} from "components/_defaultComponents/ResponsiveDataTable";

const CreateRequestForm = ({onClose = () => {}, recall = () => {}}) => {
    const stores = useStore();
    const [form] = Form.useForm();
    const [equipments, setEquipments] = useState([]);
    const [selectedEquipments, setSelectedEquipments] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);

    // Fetch equipments
    const [, equipmentList] = useFetch({
        fetchFn: stores.equipmentStore.listEquipments.bind(null, {
            limit: 1000,
            page: 1
        }),
        dependencies: []
    });

    useEffect(() => {
        if (equipmentList?.data?.result) {
            setEquipments(equipmentList.data.result);
        }
    }, [equipmentList]);

    const handleCityChange = value => {
        setSelectedCity(value);
        setSelectedDistrict(null);
        form.setFieldsValue({districtId: undefined, neighborhoodId: undefined});
    };

    const handleDistrictChange = value => {
        setSelectedDistrict(value);
        form.setFieldsValue({neighborhoodId: undefined});
    };

    const handleEquipmentSelect = (checked, equipment, variation) => {
        if (checked) {
            setSelectedEquipments(prev => [
                ...prev,
                {
                    equipmentId: equipment._id,
                    variationId: variation._id,
                    equipmentName: equipment.name,
                    variationSize: variation.size,
                    variationColor: variation.color,
                    quantity: 1
                }
            ]);
        } else {
            setSelectedEquipments(prev =>
                prev.filter(
                    item =>
                        !(item.equipmentId === equipment._id && item.variationId === variation._id)
                )
            );
        }
    };

    const handleQuantityChange = (equipmentId, variationId, quantity) => {
        setSelectedEquipments(prev =>
            prev.map(item =>
                item.equipmentId === equipmentId && item.variationId === variationId
                    ? {...item, quantity}
                    : item
            )
        );
    };

    const resetForm = () => {
        form.resetFields();
        setSelectedEquipments([]);
        setSelectedCity(null);
        setSelectedDistrict(null);
    };

    const handleSubmit = async values => {
        const requestedEquipments = selectedEquipments.filter(item => item.quantity > 0);

        if (requestedEquipments.length === 0) {
            return stores.appStore.openErrorModal({
                content: "En az bir ekipman seçmelisiniz."
            });
        }

        const result = await stores.equipmentRequestStore.createRequest({
            zoneId: values.zoneId,
            requesterName: values.requesterName,
            requesterPhone: values.requesterPhone,
            requesterTckn: values.requesterTckn,
            cityId: values.cityId,
            districtId: values.districtId,
            neighborhoodId: values.neighborhoodId,
            address: values.address,
            requestedEquipments
        });

        if (result) {
            resetForm();
            onClose();
            recall();
        }
    };

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    const isEquipmentSelected = (equipmentId, variationId) => {
        return selectedEquipments.some(
            item => item.equipmentId === equipmentId && item.variationId === variationId
        );
    };

    const getQuantity = (equipmentId, variationId) => {
        const item = selectedEquipments.find(
            item => item.equipmentId === equipmentId && item.variationId === variationId
        );
        return item?.quantity || 1;
    };

    return useObserver(() => (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        label="Bölge"
                        name="zoneId"
                        rules={[{required: true, message: "Bölge seçiniz"}]}
                    >
                        <ZoneSelect
                            value={form.getFieldValue("zoneId")}
                            onChange={value => form.setFieldsValue({zoneId: value})}
                            showTitle={false}
                            marginBottom="0"
                            placeholder="Bölge Seçiniz"
                            showSyUser={true}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Ad Soyad"
                        name="requesterName"
                        rules={[{required: true, message: "Ad Soyad giriniz"}]}
                    >
                        <Input placeholder="Ad Soyad" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        label="Telefon"
                        name="requesterPhone"
                        rules={[{required: true, message: "Telefon giriniz"}]}
                    >
                        <MaskedInput
                            mask="0(###)-###-####"
                            name="requesterPhone"
                            placeholder="0(555)-555-5555"
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Kimlik Numarası"
                        name="requesterTckn"
                        rules={[
                            {required: true, message: "Kimlik numarası giriniz"},
                            {len: 11, message: "Kimlik numarası 11 haneli olmalıdır"}
                        ]}
                    >
                        <Input placeholder="Kimlik Numarası" maxLength={11} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        label="Şehir"
                        name="cityId"
                        rules={[{required: true, message: "Şehir seçiniz"}]}
                    >
                        <DynamicCityFilter
                            value={form.getFieldValue("cityId")}
                            onChange={value => {
                                form.setFieldsValue({cityId: value});
                                handleCityChange(value);
                            }}
                            showLabel={false}
                            selectMarginBottom="0"
                            placeholder="Şehir Seçiniz"
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label="İlçe"
                        name="districtId"
                        rules={[{required: true, message: "İlçe seçiniz"}]}
                    >
                        <DynamicDistrictFilter
                            value={form.getFieldValue("districtId")}
                            cityId={selectedCity}
                            onChange={value => {
                                form.setFieldsValue({districtId: value});
                                handleDistrictChange(value);
                            }}
                            showLabel={false}
                            selectMarginBottom="0"
                            placeholder="İlçe Seçiniz"
                            disabled={!selectedCity}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label="Mahalle"
                        name="neighborhoodId"
                        rules={[{required: true, message: "Mahalle seçiniz"}]}
                    >
                        <DynamicNeighborhoodFilter
                            value={form.getFieldValue("neighborhoodId")}
                            districtId={selectedDistrict}
                            onChange={value => {
                                form.setFieldsValue({neighborhoodId: value});
                            }}
                            showLabel={false}
                            selectMarginBottom="0"
                            placeholder="Mahalle Seçiniz"
                            disabled={!selectedDistrict}
                            allowClear
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item
                label="Adres"
                name="address"
                rules={[{required: true, message: "Adres giriniz"}]}
            >
                <Input.TextArea rows={3} placeholder="Adres" />
            </Form.Item>

            <Divider>Ekipman Seç</Divider>

            <div style={{maxHeight: "400px", overflowY: "auto"}}>
                <ResponsiveDataTableStyled
                    dataSource={equipments.flatMap(equipment =>
                        equipment.variations?.map(variation => ({
                            key: `${equipment._id}-${variation._id}`,
                            equipmentId: equipment._id,
                            variationId: variation._id,
                            equipmentName: equipment.name,
                            size: variation.size || "-",
                            color: variation.color || "-",
                            price: variation.price || 0
                        }))
                    )}
                    columns={[
                        {
                            title: "Seç",
                            key: "select",
                            width: 60,
                            render: (_, record) => (
                                <Checkbox
                                    checked={isEquipmentSelected(
                                        record.equipmentId,
                                        record.variationId
                                    )}
                                    onChange={e => {
                                        const equipment = equipments.find(
                                            eq => eq._id === record.equipmentId
                                        );
                                        const variation = equipment?.variations?.find(
                                            v => v._id === record.variationId
                                        );
                                        if (equipment && variation) {
                                            handleEquipmentSelect(
                                                e.target.checked,
                                                equipment,
                                                variation
                                            );
                                        }
                                    }}
                                />
                            ),
                            onCell: () => ({
                                "data-label": "Seç"
                            })
                        },
                        {
                            title: "Ekipman Adı",
                            dataIndex: "equipmentName",
                            key: "equipmentName",
                            onCell: () => ({
                                "data-label": "Ekipman Adı"
                            })
                        },
                        {
                            title: "Beden",
                            dataIndex: "size",
                            key: "size",
                            width: 100,
                            onCell: () => ({
                                "data-label": "Beden"
                            })
                        },
                        {
                            title: "Renk",
                            dataIndex: "color",
                            key: "color",
                            width: 100,
                            onCell: () => ({
                                "data-label": "Renk"
                            })
                        },
                        {
                            title: "Fiyat",
                            dataIndex: "price",
                            key: "price",
                            width: 100,
                            render: price => `₺${price.toFixed(2)}`,
                            onCell: () => ({
                                "data-label": "Fiyat"
                            })
                        },
                        {
                            title: "Adet",
                            key: "quantity",
                            width: 100,
                            render: (_, record) => {
                                const isSelected = isEquipmentSelected(
                                    record.equipmentId,
                                    record.variationId
                                );
                                return isSelected ? (
                                    <InputNumber
                                        min={1}
                                        max={100}
                                        value={getQuantity(record.equipmentId, record.variationId)}
                                        onChange={value =>
                                            handleQuantityChange(
                                                record.equipmentId,
                                                record.variationId,
                                                value
                                            )
                                        }
                                        style={{width: "100%"}}
                                    />
                                ) : null;
                            },
                            onCell: () => ({
                                "data-label": "Adet"
                            })
                        }
                    ]}
                    pagination={false}
                    size="small"
                    scroll={{y: 300}}
                />
            </div>

            <Divider />

            <Form.Item style={{marginBottom: 0, textAlign: "right"}}>
                <Button onClick={handleCancel} style={{marginRight: 8}}>
                    İptal
                </Button>
                <Button type="primary" htmlType="submit">
                    Kaydet
                </Button>
            </Form.Item>
        </Form>
    ));
};

export default CreateRequestForm;
